'use client';

/**
 * RfiDetailsPanel — Thin wrapper for entity details in RFI mode
 *
 * Delegates rendering to the schema-driven DetailsPanel for all entity types.
 * Provides the sticky header + loading chrome expected by the copilot layout.
 *
 * @layer presentation
 * @arch_component_type panel
 * @arch_domain rfi
 */

import { useState } from 'react';
import { FileText, FolderKanban, MessageSquare, Loader2, Paperclip, Pencil, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useStrategyData } from '@/lib/api/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { api } from '@/lib/api/client';
import { toast } from 'sonner';
import { useRfqActions } from '@/lib/hooks/use-rfq-actions';
import { DetailsPanel } from './details/details-panel';
import { PlatformBadge } from './details/shared';

// ── Config ───────────────────────────────────────────────────────

const ENTITY_CONFIG: Record<string, { icon: typeof FileText; label: string; color: string }> = {
  rfq: { icon: FileText, label: 'RFQ', color: 'text-warning' },
  project: { icon: FolderKanban, label: 'Project', color: 'text-primary' },
  message: { icon: MessageSquare, label: 'Message', color: 'text-info' },
};

// ── Props ────────────────────────────────────────────────────────

interface RfiDetailsPanelProps {
  entityType: string | null;
  entityId: string | null;
}

// ── Component ────────────────────────────────────────────────────

export function RfiDetailsPanel({ entityType, entityId }: RfiDetailsPanelProps) {
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);

  // Message details are rendered directly from store (no BFF call)
  if (entityType === 'message' && selectedNode?.type === 'message') {
    return <MessageDetailsPanel node={selectedNode} />;
  }

  return <BffDetailsPanel entityType={entityType} entityId={entityId} />;
}

// ── Message Details (from store, no API) ─────────────────────────

function MessageDetailsPanel({ node }: { node: { id: string; label: string; properties?: Record<string, unknown> } }) {
  const props = node.properties ?? {};
  const body = (props.body as string) ?? '';
  const authorName = (props.author_name as string) ?? 'Unknown';
  const platform = (props.platform as string) ?? '';
  const messageType = (props.message_type as string) ?? '';
  const createdDate = (props.created_date as string) ?? '';
  const hasAttachments = props.has_attachments as boolean | null;
  const attachmentCount = (props.attachment_count as number) ?? 0;
  const cdk = (props.cdk as string) ?? node.id;

  const timestamp = createdDate
    ? new Date(createdDate).toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  return (
    <div className="flex flex-col h-full">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur-sm px-3 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <MessageSquare className="h-4 w-4 text-info" />
          <span className="text-sm font-semibold truncate">{authorName}</span>
        </div>
        <Badge variant="outline" className="text-[9px] px-1.5 py-0 shrink-0">Message</Badge>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto scrollbar-thin p-4 space-y-4">
        {/* Meta row */}
        <div className="flex items-center gap-2 flex-wrap">
          {platform && <PlatformBadge platform={platform} />}
          {messageType && (
            <Badge variant="secondary" className="text-[10px]">{messageType}</Badge>
          )}
          {hasAttachments && attachmentCount > 0 && (
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <Paperclip className="h-3 w-3" />
              {attachmentCount} file{attachmentCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Timestamp */}
        {timestamp && (
          <p className="text-xs text-muted-foreground">{timestamp}</p>
        )}

        {/* Body */}
        {body ? (
          <div className="glass-card rounded-lg p-3">
            <p className="text-sm text-foreground/90 whitespace-pre-wrap break-words leading-relaxed">
              {body}
            </p>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">No message body</p>
        )}

        {/* CDK identifier */}
        <div className="pt-2 border-t border-border/30">
          <p className="text-[10px] text-muted-foreground font-mono truncate" title={cdk}>
            CDK: {cdk}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── BFF Details (existing flow) ──────────────────────────────────

function BffDetailsPanel({ entityType, entityId }: { entityType: string | null; entityId: string | null }) {
  const { data, isLoading } = useStrategyData<Record<string, unknown>>(
    'rfi_details',
    { entity_type: entityType ?? '', entity_sid: entityId ?? '' },
    !!entityType && !!entityId,
  );

  if (!entityType || !entityId) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground p-4">
        <FileText className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">Select an item to see details</p>
      </div>
    );
  }

  const cfg = ENTITY_CONFIG[entityType] ?? { icon: FileText, label: entityType, color: 'text-muted-foreground' };
  const Icon = cfg.icon;
  const bffData = data?.data ?? {};
  const entityName = (bffData.name ?? bffData.rfq_name ?? bffData.project_name ?? '') as string;

  const { editRfq } = useRfqActions();
  const queryClient = useQueryClient();
  const [isCancelling, setIsCancelling] = useState(false);

  // Edit-load is the hook's job (use-rfq-actions.editRfq) — ONE
  // implementation shared with the explorer kebab; this panel used to
  // carry a near-duplicate of the field mapping.
  const handleEditRfq = () => {
    if (!entityId) return;
    void editRfq(entityId);
  };

  const handleCancelRfq = async () => {
    const odooId = bffData.odoo_id as number | undefined;
    if (!odooId || isCancelling) return;
    if (!confirm('Cancel this RFQ? This cannot be undone.')) return;

    setIsCancelling(true);
    try {
      const result = await api.getStrategyData<{ rfq_id: number; message: string }>(
        'rfq_delete', { rfq_id: odooId }
      );
      if (result.success) {
        toast.success(result.data?.message || 'RFQ cancelled');
        queryClient.invalidateQueries({ queryKey: ['strategy-data', 'rfi_details'] });
        queryClient.invalidateQueries({ queryKey: ['obeya-projects'] });
      } else {
        toast.error(result.error || 'Cancel failed');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Cancel failed');
    } finally {
      setIsCancelling(false);
    }
  };

  const rfqState = bffData.state as string | undefined;
  const canCancel = rfqState && rfqState !== 'cancel' && rfqState !== 'done';

  return (
    <div className="flex flex-col h-full">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur-sm px-3 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={`h-4 w-4 ${cfg.color}`} />
          <span className="text-sm font-semibold truncate">
            {isLoading ? 'Loading...' : entityName || cfg.label}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {entityType === 'rfq' && !isLoading && bffData.odoo_id && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[10px]"
                onClick={handleEditRfq}
              >
                <Pencil className="h-3 w-3 mr-1" />
                Edit
              </Button>
              {canCancel && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-[10px] text-destructive hover:text-destructive"
                  onClick={handleCancelRfq}
                  disabled={isCancelling}
                >
                  {isCancelling
                    ? <Loader2 className="h-3 w-3 animate-spin" />
                    : <XCircle className="h-3 w-3 mr-1" />
                  }
                  Cancel
                </Button>
              )}
            </>
          )}
          <Badge variant="outline" className="text-[9px] px-1.5 py-0">
            {cfg.label}
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto scrollbar-thin p-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <Loader2 className="h-8 w-8 mb-2 animate-spin" />
            <p className="text-sm">Loading details...</p>
          </div>
        ) : (
          <DetailsPanel
            entityType={entityType}
            entityId={entityId}
            data={{ ...bffData, semantic_id: bffData.entity_sid ?? entityId } as Record<string, unknown>}
            isLoading={false}
          />
        )}
      </div>
    </div>
  );
}
